import DineModeTabs from "@/components/dine-mode-tabs/DineModeTabs";
import { ArrowDown01Icon, ArrowLeft02Icon, ArrowRight02Icon, ArrowUp01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Drawer, Stack, Typography, Box, Divider, Checkbox, Button, TextField } from "@mui/material";
import OrderItemsList from "./OrderItemsList";
import Price from "@/components/Price";
import { getOrderSubtotal, getOrderTaxes, getOrderTotal } from "@/utils/order";
import { OrderItem } from "@/types";
import { RootState } from "@/store/store";
import { useAppDispatch, useAppSelector, useResponsive } from "@/hooks";
import { EmptyState } from "@/components";
import { useMemo } from "react";
import { grey } from "@mui/material/colors";
import { addTips, removeTips } from "../slice";
import { useFormikContext } from "formik";
import { ICheckout } from "@/pages/organization-orders/add-orders/AdminCheckout";
import DataCell from "@/components/DataCell";
import useServiceFee from "@/hooks/useServiceFee";
import { closeSummary, openSummary } from "@/store/uiSlice";

interface Props {
  isOpen: boolean;
  activeStep?: number;
  steps: { id: string; title: string; description?: string; component: React.ComponentType<any> }[];
  onClose?: () => void;
  onBack?: () => void;
  onNext?: (values: ICheckout) => void;
}

const OrderAdminDrawer = ({ isOpen, onClose, activeStep, onBack, onNext, steps }: Props) => {
  const order = useAppSelector((state: RootState) => state.order);
  const organizationId = useAppSelector((state) => state.ui.activeOrganization);
  const organization = useAppSelector((state) => state.organizations[organizationId]);
  const { isMobile } = useResponsive();
  const dispatch = useAppDispatch();
  const { values, handleSubmit, setFieldValue } = useFormikContext<ICheckout>();
  const service = useServiceFee();
  const isLastStep = activeStep === steps.length - 1;
  const orderFlow = useAppSelector((state) => state.order.flow);
  const isExpanded = useAppSelector((state) => state.ui.isSummaryOpen);

  const deliveryFeeToUse = orderFlow === "delivery" && organization?.settings?.services?.delivery?.type !== "fixed" ? Number(values.deliveryFee || 0) : Number(service.fee || 0);

  const showTaxes = useMemo(() => {
    return Boolean(organization?.settings?.includeTax);
  }, [organization?.settings?.includeTax]);

  const tips = useMemo(() => {
    if (orderFlow === "dine-in") {
      return order?.areTipsIncluded ? getOrderSubtotal(order.items as OrderItem[]) * (order?.tipPercentage / 100) : 0;
    }
    return order?.areTipsIncluded ? 0 : 0;
  }, [orderFlow, order?.areTipsIncluded, order.items, order?.tipPercentage]);

  const toggleTips = () => {
    dispatch(order.areTipsIncluded ? removeTips() : addTips());
  };

  return (
    <Drawer
      anchor={"bottom"}
      open={isOpen}
      onClose={onClose}
      variant="permanent"
      slotProps={{
        paper: {
          sx: {
            borderTopLeftRadius: isExpanded ? "28px" : 0,
            borderTopRightRadius: isExpanded ? "28px" : 0,
            width: isMobile ? "100%" : "50%",
          },
        },
      }}
    >
      <Box>
        {isExpanded && order?.items?.length ? (
          <Stack p={2}>
            <Stack spacing={2}>
              <Typography variant="h3" textAlign={"left"}>
                Pedido
              </Typography>
              <DineModeTabs />
            </Stack>

            <Stack>
              <OrderItemsList items={order.items} />
              <Divider color={grey[300]} />
              <Stack spacing={1} px={4} py={3}>
                {/* SUBTOTAL */}
                {showTaxes ? (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h4" fontSize={"18px"}>
                      Subtotal
                    </Typography>
                    <Typography variant="h4" fontSize={"18px"}>
                      <Price currency="NIO">{getOrderSubtotal(order.items)}</Price>
                    </Typography>
                  </Stack>
                ) : null}

                {/* TAXES */}
                {showTaxes ? (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h4" fontSize={"18px"}>
                      IVA
                    </Typography>
                    <Typography variant="h4" fontSize={"18px"}>
                      <Price currency="NIO">{getOrderTaxes(order.items)}</Price>
                    </Typography>
                  </Stack>
                ) : null}

                {/* TIPS */}
                {orderFlow === "dine-in" ? (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography>Propina {order.tipPercentage}% (voluntaria)</Typography>
                      <Checkbox checked={order.areTipsIncluded} value={!order.areTipsIncluded} onChange={toggleTips} sx={{ padding: 0 }} />
                    </Stack>
                    <Typography>
                      <Price currency="NIO">{tips}</Price>
                    </Typography>
                  </Stack>
                ) : null}

                {/* SERVICE FEE */}
                {orderFlow === "delivery" ? (
                  organization?.settings?.services?.delivery?.type === "fixed" ? (
                    <DataCell label="Envío" value={<Price currency="NIO">{values.deliveryFee}</Price>} />
                  ) : (
                    <TextField label="Envío" value={values.deliveryFee} size="small" type="number" onChange={(e) => setFieldValue("deliveryFee", e.target.value)} />
                  )
                ) : null}

                {/* TOTAL */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h3" fontWeight="bold">
                    Total
                  </Typography>
                  <Typography variant="h3" fontWeight="bold">
                    <Price currency="NIO">{getOrderTotal(order.items as OrderItem[], showTaxes, deliveryFeeToUse)}</Price>
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        ) : null}

        {isExpanded && !order?.items?.length ? (
          <Stack p={4} alignItems="center" spacing={2}>
            <EmptyState
              title="Tu cuenta está vacía"
              description="Cuando agregues algo a tu cuenta aparecerá justo aquí"
              action={{
                children: "Agregar",
                onClick: () => dispatch(openSummary()),
              }}
            />
          </Stack>
        ) : null}

        <Stack direction="row" spacing={2} padding={2}>
          <Stack>
            <Button
              variant="outlined"
              startIcon={<HugeiconsIcon icon={!isExpanded ? ArrowUp01Icon : ArrowDown01Icon} />}
              color={"primary"}
              onClick={() => (isExpanded ? dispatch(closeSummary()) : dispatch(openSummary()))}
            >
              Orden
            </Button>
          </Stack>
          <Stack flex={1} direction={"row"} />
          {activeStep! > 0 && (
            <Button color="inherit" startIcon={!isMobile ? <HugeiconsIcon icon={ArrowLeft02Icon} /> : null} onClick={onBack}>
              Regresar
            </Button>
          )}

          <Button
            color="primary"
            endIcon={!isMobile ? <HugeiconsIcon icon={ArrowRight02Icon} /> : null}
            onClick={() => (isLastStep ? handleSubmit() : onNext(values || ({} as ICheckout)))}
          >
            {isLastStep ? "Finalizar" : "Continuar"}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default OrderAdminDrawer;
